/**
 * SettingsPolicies — Iter 191y · Customer Notifications › Policies
 *
 * Vendor-editable Returns, Privacy, and Terms-of-Service policies with
 * pre-written templates that can be applied per-policy.
 *
 * Persistence:
 *   GET  /api/builder/policies
 *   PUT  /api/builder/policies
 *   POST /api/builder/policies/apply-template/{key}
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ScrollText, Save, Loader2, RotateCcw, Eye, ExternalLink,
  RefreshCw, Lock, FileText,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { GlassCard, DashboardBackdrop, GradientIconTile } from '@/components/dashboard/dashboard-ui';
import { themedConfirm } from '@/lib/themed-dialog';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ICONS = {
  returns: RefreshCw,
  privacy: Lock,
  terms:   FileText,
};

const RAMPS = {
  returns: 'emerald',
  privacy: 'violet',
  terms:   'cyan',
};

export default function SettingsPolicies() {
  const [policies, setPolicies] = useState({});
  const [subdomain, setSubdomain] = useState('');
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewKey, setPreviewKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: pol }, { data: site }] = await Promise.all([
          axios.get(`${API}/builder/policies`,   { headers: auth() }),
          axios.get(`${API}/builder/my-site`,    { headers: auth() }).catch(() => ({ data: {} })),
        ]);
        setPolicies(pol?.policies || {});
        setSubdomain(site?.subdomain || '');
        // Expand the first policy by default
        const first = Object.keys(pol?.policies || {})[0];
        if (first) setExpanded({ [first]: true });
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const setPolicy = (key, patch) =>
    setPolicies((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, p] of Object.entries(policies)) {
        payload[k] = { body: p.body || '', published: !!p.published };
      }
      const { data } = await axios.put(`${API}/builder/policies`, { policies: payload }, { headers: auth() });
      setPolicies(data.policies || {});
      toast.success('Policies saved');
    } catch { toast.error('Could not save policies'); }
    finally { setSaving(false); }
  };

  const applyTemplate = async (key) => {
    const proceed = await themedConfirm({
      title: `Apply the ${policies[key]?.label} template?`,
      message: 'This will overwrite your current text for this policy with the pre-written default. You can undo before saving.',
      confirmLabel: 'Apply template',
    });
    if (!proceed) return;
    setPolicy(key, { body: policies[key]?.template_body || '' });
    toast.info('Template applied — click Save to persist.');
  };

  const publicUrl = (key) => (subdomain ? `${process.env.REACT_APP_BACKEND_URL}/api/public/sites/${subdomain}/policies/${key}` : null);

  return (
    <div className="relative" data-testid="settings-policies-page">
      <DashboardBackdrop />
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
        <BackToSettings />

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Customer Notifications</p>
            <h1 className="text-3xl font-bold text-white font-syne mt-1">Policies</h1>
            <p className="text-sm text-white/65 mt-2 max-w-xl">
              Returns, Privacy, and Terms of Service. Start with a pre-written template and edit it to match
              your business, then publish so we can link to it from receipts, checkout, and your footer.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all"
            data-testid="policies-save-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save all'}
          </button>
        </div>

        {loading ? (
          <div className="text-white/50 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : Object.entries(policies).map(([key, p]) => {
          const Icon = ICONS[key] || ScrollText;
          const isOpen = !!expanded[key];
          const characters = (p.body || '').length;
          const isEmpty = !p.body?.trim();

          return (
            <GlassCard key={key} className="p-0 overflow-hidden" data-testid={`policy-card-${key}`}>
              <div className="p-5 flex items-center gap-4 border-b border-white/[0.05]">
                <GradientIconTile icon={Icon} ramp={RAMPS[key]} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-white font-bold font-syne">{p.label}</h2>
                    {p.published
                      ? <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-bold uppercase tracking-wider">Published</span>
                      : <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60 font-bold uppercase tracking-wider">Draft</span>}
                    {isEmpty && <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold uppercase tracking-wider">Empty</span>}
                  </div>
                  <p className="text-[11.5px] text-white/55 mt-0.5">{p.summary}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Switch checked={!!p.published} onCheckedChange={(v) => setPolicy(key, { published: v })} data-testid={`policy-published-${key}`} />
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                    className="text-[10.5px] uppercase tracking-wider font-bold text-white/70 hover:text-white"
                    data-testid={`policy-toggle-${key}`}
                  >
                    {isOpen ? 'Collapse' : 'Edit'}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="px-5 pb-5 pt-4 space-y-3">
                  <Textarea
                    rows={12}
                    value={p.body || ''}
                    onChange={(e) => setPolicy(key, { body: e.target.value })}
                    placeholder={`Write your ${p.label.toLowerCase()} — Markdown supported (## headings, **bold**, - lists).`}
                    className="bg-white/5 border-white/10 text-white rounded-xl font-mono text-[12.5px] leading-relaxed"
                    data-testid={`policy-body-${key}`}
                  />
                  <div className="flex items-center justify-between text-[10.5px]">
                    <p className="text-white/40">{characters.toLocaleString()} characters · Markdown supported</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => applyTemplate(key)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg font-bold uppercase tracking-wider bg-white/[0.06] hover:bg-white/[0.1] text-white/80 border border-white/10"
                        data-testid={`policy-apply-template-${key}`}
                      >
                        <RotateCcw size={11} /> Apply template
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewKey(key)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg font-bold uppercase tracking-wider bg-[#A78BFA] text-[#0E0E23] hover:bg-white"
                        data-testid={`policy-preview-${key}`}
                      >
                        <Eye size={11} /> Preview
                      </button>
                      {p.published && publicUrl(key) && (
                        <a
                          href={publicUrl(key)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg font-bold uppercase tracking-wider text-white/60 hover:text-white"
                          data-testid={`policy-public-link-${key}`}
                        >
                          <ExternalLink size={11} /> Public URL
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </GlassCard>
          );
        })}

        <GlassCard className="p-5 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl grid place-items-center bg-[#8B5CF6]/15 border border-[#8B5CF6]/25 flex-shrink-0">
            <ScrollText size={15} className="text-[#A78BFA]" />
          </div>
          <div>
            <p className="text-white text-sm font-bold">Where do these show up?</p>
            <p className="text-[11.5px] text-white/60 mt-1">
              Published policies get a stable public URL (see the icon on each card). Add links to them
              from your storefront footer, checkout page, and order confirmation email. Unpublished policies
              stay hidden.
            </p>
          </div>
        </GlassCard>
      </div>

      {previewKey && <PolicyPreview policy={policies[previewKey]} onClose={() => setPreviewKey(null)} />}
    </div>
  );
}

function PolicyPreview({ policy, onClose }) {
  const rendered = renderMarkdown(policy?.body || '');
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="policy-preview-modal">
      <div className="absolute inset-0 bg-[#08081A]/80 backdrop-blur-md" />
      <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 bg-white p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Preview · buyer view</p>
            <h3 className="text-slate-900 font-bold text-lg mt-1">{policy?.label}</h3>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-900 text-xs uppercase tracking-wider font-bold" data-testid="policy-preview-close">Close</button>
        </div>
        <div className="p-8 text-slate-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: rendered }} />
      </div>
    </div>
  );
}

// Tiny Markdown renderer — headings, bold, lists, paragraphs. No dependency needed.
function renderMarkdown(md) {
  if (!md) return '<p class="text-slate-500 italic">Empty policy.</p>';
  const escape = (s) => s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = md.split('\n');
  let html = '';
  let inList = false;
  const closeList = () => { if (inList) { html += '</ul>'; inList = false; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { closeList(); continue; }
    if (line.startsWith('# '))  { closeList(); html += `<h1 class="text-2xl font-bold mt-6 mb-3 text-slate-900">${escape(line.slice(2))}</h1>`;  continue; }
    if (line.startsWith('## ')) { closeList(); html += `<h2 class="text-lg font-bold mt-5 mb-2 text-slate-900">${escape(line.slice(3))}</h2>`;   continue; }
    if (line.startsWith('- '))  {
      if (!inList) { html += '<ul class="list-disc list-inside space-y-1 my-3 text-sm">'; inList = true; }
      html += `<li>${escape(line.slice(2)).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</li>`;
      continue;
    }
    closeList();
    const p = escape(line).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html += `<p class="text-sm mb-3">${p}</p>`;
  }
  closeList();
  return html;
}
